/**
 * VRG-723 — the IMPORT FIELD REPORT: which fields a demo's Propose or a single-product Import could
 * not pull in from the prospect's site, each with the reason, so the AE knows what to fill by hand
 * before issuing instead of finding a blank on the certificate after the send.
 *
 * Two levels, and the split is the UI's:
 *   - per PRODUCT (images, price, SKU, description, manufacture date), shown on the product card;
 *   - per BRAND (logo, colors, fonts), shown in the Create demo modal above the Theme card.
 *
 * The API decides the gaps (it is the only side that knows WHY: which path read the product, what
 * the page or store API returned); the platform renders them verbatim. A field that was read is
 * simply absent from the list, so an empty list means "everything came through".
 */
export const PRODUCT_IMPORT_FIELDS = ['images', 'price', 'sku', 'description', 'manufacture_date'] as const;
export type ProductImportField = (typeof PRODUCT_IMPORT_FIELDS)[number];

export const BRAND_IMPORT_FIELDS = ['logo', 'colors', 'fonts'] as const;
export type BrandImportField = (typeof BRAND_IMPORT_FIELDS)[number];

export type ImportField = ProductImportField | BrandImportField;

export const IMPORT_FIELD_LABELS: Readonly<Record<ImportField, string>> = {
  images: 'Images',
  price: 'Price',
  sku: 'SKU',
  description: 'Description',
  manufacture_date: 'Manufacture date',
  logo: 'Logo',
  colors: 'Colors',
  fonts: 'Fonts',
};

/** One field that was not pulled in, and why. `reason` is a full sentence the UI shows as is. */
export interface ImportFieldGap<F extends ImportField = ImportField> {
  field: F;
  reason: string;
}

export const isProductImportField = (v: unknown): v is ProductImportField =>
  typeof v === 'string' && (PRODUCT_IMPORT_FIELDS as readonly string[]).includes(v);

/**
 * The reason a manufacture date is never pulled in. One sentence, shared, because it is the same on
 * every path: storefront pages and the Shopify storefront API do not carry one, and the importer
 * does not read schema.org `productionDate`.
 */
export const MANUFACTURE_DATE_NOT_READ_REASON =
  "Not read from store pages: storefronts rarely publish one. Add it on the product if you know it.";
